import { BaseTreeService } from './base-tree.service';
import { inject, Injectable } from '@angular/core';
import {
  BaseTreeNode,
  isSubsectionOptionNode,
  isSubsectionTopicNode,
  ProjectRootNode,
  ProjectSectionNode,
  SectionNode,
  SubsectionNode,
  SubsectionFileNode,
  SubsectionOptionNode,
  SubsectionRootNode,
  SubsectionTopicNode,
  SubsectionVersionNode,
  TreeNodeType,
} from '../models/tree-node';
import {
  FileInfo,
  OptionInfo,
  OptionVersion,
  OptionVersionSimple,
  Project,
  ProjectSubsection,
  TopicInfo,
} from '../../shared/interfaces';
import { TreeSortMode } from '../../shared/models';
import { Observable, takeUntil } from 'rxjs';
import { SubsectionService } from '../../shared/services/subsection.service';
import { tap } from 'rxjs/operators';
import { ActiveNodeUpdate, GetTreeFilesParams, NavigationResult, SubsectionTreeState } from '../models';
import { SubsectionTreeNavigationService } from './subsection-tree/subsection-tree-navigation.service';
import {
  createFileNode,
  createOptionNode,
  createTopicNode,
  createVersionNode,
} from './subsection-tree/project-subsection-tree-builders';

@Injectable({
  providedIn: 'root',
})
export class SubsectionTreeService extends BaseTreeService<SubsectionNode> {
  protected override state: SubsectionTreeState = {
    root: null,
    paths: {},
    selectedNode: null,
    expandedNodes: [],
    versionData: null,
  };
  private readonly subsectionService: SubsectionService = inject(SubsectionService);
  private readonly navigationService: SubsectionTreeNavigationService = inject(SubsectionTreeNavigationService);
  private previousVersionNode: SubsectionVersionNode | null = null;

  public buildProjectTree(project: Project): void {
    const rootNode: ProjectRootNode = {
      id: project.id,
      name: project.name,
      type: TreeNodeType.ProjectRoot,
      isExpandable: true,
      isSelected: true,
      isExpanded: true,
      children: [] as SubsectionNode[],
      parent: null,
      depth: 0,
      data: project,
    };

    this.state = {
      root: rootNode,
      paths: {
        [rootNode.id]: [rootNode.id],
      },
      selectedNode: rootNode,
      expandedNodes: [],
      versionData: null,
    };

    (project.sections || []).forEach((section: Project['sections'][number]) => {
      if (!section.projectSubSections?.length) {
        return;
      }

      const sectionNode: ProjectSectionNode = {
        id: section.id,
        name: section.name,
        type: TreeNodeType.ProjectSection,
        isExpandable: true,
        isSelected: false,
        isExpanded: false,
        children: [] as SubsectionNode[],
        parent: rootNode,
        depth: rootNode.depth + 1,
        data: section,
      };
      this.addNodeToParent(rootNode, sectionNode, false);

      (section.projectSubSections || []).forEach((subsection: ProjectSubsection) => {
        const subsectionNode: SubsectionRootNode = {
          id: subsection.id,
          name: subsection.name,
          type: TreeNodeType.SubsectionRoot,
          isExpandable: true,
          isSelected: false,
          isExpanded: true,
          children: [],
          projectId: project.id,
          parent: sectionNode,
          depth: sectionNode.depth + 1,
          data: subsection,
        };
        this.addNodeToParent(sectionNode, subsectionNode, false);
      });
    });

    this.updateState(this.state);
  }

  public buildTopicOptionNodes(data: {
    topics: TopicInfo[];
    options: OptionInfo[];
    sortMode: TreeSortMode;
    projectId: string;
    parent: SubsectionNode;
  }): void {
    const { parent, topics, options, sortMode } = data;

    topics.forEach((topic: TopicInfo) => {
      const topicNode: SubsectionTopicNode = createTopicNode(topic, parent);
      this.addNode(parent.id, topicNode);

      const relatedOptions: OptionInfo[] = options.filter((option: OptionInfo) => option.topicId === topic.topicId);
      relatedOptions.forEach((option: OptionInfo) => {
        const optionNode: SubsectionOptionNode = createOptionNode(option, topicNode);
        this.addNode(topic.topicId, optionNode);

        const versions: OptionVersionSimple[] = option.simpleVersions || [];
        versions.forEach((version: OptionVersionSimple) => {
          const versionNode: SubsectionVersionNode = createVersionNode(version, optionNode);
          this.addNode(option.optionId, versionNode);
        });
      });
    });

    this.sortTree(sortMode);
  }

  public buildTree(data: {
    subsectionInfo: ProjectSubsection;
    topics: TopicInfo[];
    options: OptionInfo[];
    sortMode: TreeSortMode;
    projectId: string;
  }): void {
    const { subsectionInfo, topics, options, sortMode, projectId } = data;

    const treeRootNode: SubsectionRootNode = {
      id: subsectionInfo.id,
      name: subsectionInfo.name,
      type: TreeNodeType.SubsectionRoot,
      data: subsectionInfo,
      children: [],
      parent: null,
      projectId: projectId,
      isExpandable: true,
      isSelected: true,
      isExpanded: true,
      depth: 0,
    };

    // Initialize state
    this.state = {
      root: treeRootNode,
      paths: {
        [subsectionInfo.id]: [treeRootNode.id],
      },
      selectedNode: treeRootNode,
      expandedNodes: [],
      versionData: null,
    };
    this.updateState(this.state);

    this.buildTopicOptionNodes({
      parent: treeRootNode,
      topics,
      options,
      sortMode,
      projectId,
    });
  }

  public addTopic(topic: TopicInfo): SubsectionNode | null {
    if (!this.state.root) return null;

    const topicNode: SubsectionTopicNode = createTopicNode(topic, this.state.root);
    return this.addNode(this.state.root.id, topicNode);
  }

  public addOption(topicId: string, option: OptionInfo): SubsectionNode | null {
    const topicNode: SubsectionTopicNode | null = this.findNodeById(topicId) as SubsectionTopicNode | null;
    if (!topicNode) return null;

    const optionNode: SubsectionOptionNode = createOptionNode(option, topicNode);
    return this.addNode(topicId, optionNode);
  }

  public addVersion(optionId: string, version: OptionVersionSimple): SubsectionNode | null {
    const optionNode: SubsectionOptionNode | null = this.findNodeById(optionId) as SubsectionOptionNode | null;
    if (!optionNode) return null;

    const versionNode: SubsectionVersionNode = createVersionNode(version, optionNode);
    return this.addNode(optionId, versionNode);
  }

  public addFileNodeByParentId(versionId: string, file: FileInfo): SubsectionNode | null {
    const versionNode: SubsectionVersionNode | null = this.findNodeById(versionId) as SubsectionVersionNode | null;
    if (!versionNode) return null;

    return this.addFileNode(versionNode, file);
  }

  public addFileNode(versionNode: SubsectionNode, file: FileInfo): SubsectionNode | null {
    const fileNode: SubsectionFileNode = createFileNode(file, versionNode);
    return this.addNodeToParent(versionNode, fileNode);
  }

  public updateTopic(topicId: string, topic: TopicInfo): SubsectionNode | null {
    return this.updateNode(topicId, {
      name: topic.title,
      data: topic,
    } as Partial<SubsectionNode>);
  }

  public updateOption(optionId: string, option: OptionInfo): SubsectionNode | null {
    return this.updateNode(optionId, {
      name: option.title,
      data: option,
    } as Partial<SubsectionNode>);
  }

  public updateVersion(versionId: string, version: OptionVersionSimple): SubsectionNode | null {
    return this.updateNode(versionId, {
      name: version.name,
      data: version,
    } as Partial<SubsectionNode>);
  }

  public reorderVersions(optionId: string, orderedVersionNumbers: number[]): void {

    const optionNode: SubsectionOptionNode | null = this.findNodeById(optionId) as SubsectionOptionNode | null;
    if (!optionNode) {
      return;
    }

    const orderedVersionSet = new Set(orderedVersionNumbers);

    for (let i = optionNode.children.length - 1; i >= 0; i--) {
      const versionNode = optionNode.children[i] as SubsectionVersionNode;
      if (!orderedVersionSet.has(versionNode.data.versionNumber)) {
        this.deleteNode(versionNode, false);
      }
    }

    const versionOrderMap = new Map<number, number>();
    orderedVersionNumbers.forEach((versionNumber, index) => {
      versionOrderMap.set(versionNumber, index);
    });

    optionNode.children.sort((a, b) => {
      const versionA = a as SubsectionVersionNode;
      const versionB = b as SubsectionVersionNode;
      const orderA = versionOrderMap.get(versionA.data.versionNumber) ?? Number.MAX_SAFE_INTEGER;
      const orderB = versionOrderMap.get(versionB.data.versionNumber) ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

    this.updateState({
      root: this.state.root,
    });

  }

  public updateVersionData(versionData: OptionVersion): void {
    this.state.versionData = versionData;
    this.updateState(this.state);
  }

  public updateFile(fileId: string, file: FileInfo): SubsectionNode | null {
    return this.updateNode(fileId, {
      name: file.name,
      data: file,
    } as Partial<SubsectionNode>);
  }

  public updateActiveNodes(update: ActiveNodeUpdate): void {
    const { state } = this;
    if (!state.root) return;

    const deepestId: string | undefined =
      update.activeFileId || update.activeVersionId || update.activeOptionId || update.activeTopicId;
    if (!deepestId) {
      return;
    }

    const paths: string[] | undefined = this.state.paths[deepestId];
    if (!paths?.length) return;

    // Only reset active states for nodes that are currently active
    const currentlyActiveNodes: SubsectionNode[] = this.findActiveNodes(state.root);
    currentlyActiveNodes.forEach((node: SubsectionNode) => {
      node.isSelected = false;
      node.isExpanded = false;
    });

    // Update new active path
    let currentNode: SubsectionNode = state.root;
    currentNode.isExpanded = true;
    currentNode.isSelected = paths.length === 1;

    for (let i: number = 1; i < paths.length; i++) {
      const nextNode: SubsectionNode | undefined = currentNode.children.find(
        (child: SubsectionNode) => child.id === paths[i]
      ) as SubsectionNode;
      if (!nextNode) break;

      nextNode.isExpanded = true;
      nextNode.isSelected = i === paths.length - 1;

      if (nextNode.isSelected) {
        state.selectedNode = nextNode;
      }

      currentNode = nextNode;
    }

    this.updateState(state);
  }

  public removeFileNodes(versionId: string): SubsectionNode | null {
    const versionNode: SubsectionNode | null = this.findNodeById(versionId);
    if (!versionNode) return null;

    versionNode.children = [];

    this.updateState({
      root: this.state.root,
    });

    return versionNode;
  }

  public resetInitialActiveNodes(activeNode: SubsectionNode): void {
    const currentlyActiveNodes: SubsectionNode[] = this.findActiveNodes(activeNode);
    currentlyActiveNodes.forEach((node: SubsectionNode) => {
      node.isSelected = false;
      node.isExpanded = false;

      if (node.type === TreeNodeType.Version && node.children.length > 0) {
        node.children = [];
      }
    });

    this.setActiveNode(activeNode, false);
    this.updateState({
      root: this.state.root,
      selectedNode: activeNode,
    });
  }

  public sortTree(sortMode: TreeSortMode, updateState: boolean = true): void {
    const { root } = this.state;
    if (!root) return;

    const topicNodes: SubsectionTopicNode[] = root.children.filter(
      (child: SubsectionNode): child is SubsectionTopicNode => isSubsectionTopicNode(child)
    );

    // Sort topics
    this.sortByMode(topicNodes, sortMode, {
      updatedAt: (node: SubsectionTopicNode) => node.data.updatedAt || 0,
      lastUpload: (node: SubsectionTopicNode) => node.data.lastUpload || 0,
      firstUpload: (node: SubsectionTopicNode) => node.data.firstUpload || 0,
      title: (node: SubsectionTopicNode) => node.data.title || '',
    });

    // Sort options under each topic
    topicNodes.forEach((topicNode: SubsectionTopicNode) => {
      const optionNodes: SubsectionOptionNode[] = topicNode.children.filter(
        (child: SubsectionNode): child is SubsectionOptionNode => isSubsectionOptionNode(child)
      );

      this.sortByMode(optionNodes, sortMode, {
        updatedAt: (node: SubsectionOptionNode) => node.data.updatedAt || 0,
        lastUpload: (node: SubsectionOptionNode) => node.data.lastUploaded || 0,
        firstUpload: (node: SubsectionOptionNode) => node.data.firstUploaded || 0,
        title: (node: SubsectionOptionNode) => node.data.title || '',
      });

      //no need to sort versions
      optionNodes.forEach((optionNode: SubsectionOptionNode) => {
        // Sort files under each version
        optionNode.children.forEach((versionNode: SubsectionNode) => {
          this.sortVersionFiles(versionNode, sortMode);
        });
      });
    });

    if (updateState) {
      this.updateState({
        root: root,
      });
    }
  }

  public sortVersionFilesById(versionId: string, sortMode: TreeSortMode): void {
    const versionNode: SubsectionNode | null = this.findNodeById(versionId);
    if (!versionNode) return;

    this.sortVersionFiles(versionNode, sortMode);
    this.updateState({
      root: this.state.root,
    });
  }

  public sortVersionFiles(versionNode: SubsectionNode, sortMode: TreeSortMode): void {
    this.sortByMode(versionNode.children as SubsectionFileNode[], sortMode, {
      updatedAt: (node: SubsectionFileNode) => node.data.timeCreated || 0,
      lastUpload: (node: SubsectionFileNode) => node.data.timeCreated || 0,
      firstUpload: (node: SubsectionFileNode) => node.data.timeCreated || 0,
      title: (node: SubsectionFileNode) => node.data.displayName || '',
    });
  }

  private sortByMode<T>(
    items: T[],
    sortMode: TreeSortMode,
    getters: {
      updatedAt: (item: T) => number;
      lastUpload: (item: T) => number;
      firstUpload: (item: T) => number;
      title: (item: T) => string;
    }
  ): void {
    items.sort((a: T, b: T) => {
      switch (sortMode) {
        case TreeSortMode.lastUpdated:
          return getters.updatedAt(b) - getters.updatedAt(a);
        case TreeSortMode.lastUpload:
          return getters.lastUpload(b) - getters.lastUpload(a);
        case TreeSortMode.firstUpload:
          return getters.firstUpload(a) - getters.firstUpload(b);
        case TreeSortMode.alphabetical:
        default:
          return getters.title(a).localeCompare(getters.title(b), undefined, { sensitivity: 'base' });
      }
    });
  }

  public getHomeNavigationParams(): NavigationResult | void {
    return this.navigationService.getHomeNavigationParams(this.state);
  }

  public getTopicNavigationParams(topicNode: SubsectionTopicNode): NavigationResult {
    return this.navigationService.getTopicNavigationParams(topicNode);
  }

  public getSubsectionNavigationParams(subsectionNode: SubsectionRootNode): NavigationResult {
    return this.navigationService.getSubsectionNavigationParams(subsectionNode);
  }

  public getOptionNavigationParams(optionNode: SubsectionOptionNode): NavigationResult {
    return this.navigationService.getOptionNavigationParams(optionNode);
  }

  public getOptionNavigationWithPendingFile(optionNode: SubsectionOptionNode): NavigationResult {
    return this.navigationService.getOptionNavigationWithPendingFile(optionNode);
  }

  public getVersionNavigationParams(versionNode: SubsectionVersionNode): NavigationResult {
    return this.navigationService.getVersionNavigationParams(versionNode);
  }

  public getVersionNavigationWithPendingFile(versionNode: SubsectionVersionNode): NavigationResult {
    return this.navigationService.getVersionNavigationWithPendingFile(versionNode);
  }

  public getFileNavigationParams(fileNode: SubsectionFileNode): NavigationResult {
    return this.navigationService.getFileNavigationParams(fileNode);
  }

  public getSectionNavigationParams(sectionNode: SectionNode): NavigationResult {
    return this.navigationService.getSectionNavigationParams(sectionNode);
  }

  public getNodeNavigationParams(node: BaseTreeNode | null): NavigationResult | null {
    return this.navigationService.getNodeNavigationParams(node);
  }

  public getUpItemNavigationParams(): NavigationResult | null {
    return this.navigationService.getUpItemNavigationParams(this.state);
  }

  public getDownItemNavigationParams(): NavigationResult | null {
    return this.navigationService.getDownItemNavigationParams(this.state);
  }

  public getNextItemNavigationParams(): NavigationResult | null {
    return this.navigationService.getNextItemNavigationParams(this.state);
  }

  public getPreviousItemNavigationParams(): NavigationResult | null {
    return this.navigationService.getPreviousItemNavigationParams(this.state);
  }

  public loadVersion(args: GetTreeFilesParams): Observable<OptionVersion> {
    return this.subsectionService.getVersion(args).pipe(
      takeUntil(args.abort$),
      tap((version: OptionVersion) => {
        if (args.populateFn) {
          args.populateFn(version);
        }
        this.populateVersionFiles(version);
      })
    );
  }

  public populateVersionFiles(version: OptionVersion): void {
    const versionId = `${version.optionId}-${version.versionNumber}`;

    if (this.previousVersionNode && this.previousVersionNode.id !== versionId) {
      for (let i = this.previousVersionNode.children.length - 1; i >= 0; i--) {
        this.deleteNode(this.previousVersionNode.children[i] as SubsectionNode, false);
      }
    }

    const versionNode: SubsectionNode | null = this.findNodeById(versionId);
    if (versionNode) {
      for (let i: number = versionNode.children.length - 1; i >= 0; i--) {
        this.deleteNode(versionNode.children[i] as SubsectionNode);
      }

      version.files.forEach((file: FileInfo) => {
        this.addFileNode(versionNode, file);
      });
    }

    this.previousVersionNode = versionNode as SubsectionVersionNode;
  }

  // Add new file node
}
